import { Select, SelectItem } from '@heroui/react';
import logo_activity from '../../../assets/images/logos/logo_activity.svg';
import logo_activity_grey from '../../../assets/images/logos/logo_activity_grey.svg';
import logo_activity_white from '../../../assets/images/logos/logo_activity_white.svg';
import logo_green from '../../../assets/images/logos/logo_green.svg';
import logo_green_grey from '../../../assets/images/logos/logo_green_grey.svg';
import logo_green_white from '../../../assets/images/logos/logo_green_white.svg';
import logo_value from '../../../assets/images/logos/logo_value.svg';
import logo_value_white from '../../../assets/images/logos/logo_value_white.svg';
import logo_value_gray from '../../../assets/images/logos/logo_value_grey.svg';
import logo_dcf from '../../../assets/images/logos/logo_dcf.svg';
import logo_dcf_grey from '../../../assets/images/logos/logo_dcf_grey.svg';
import logo_dcf_white from '../../../assets/images/logos/logo_dcf_white.svg';
import logo_download from '../../../assets/images/logos/logo_download.svg';
import logo_download_grey from '../../../assets/images/logos/logo_download_grey.svg';
import logo_download_white from '../../../assets/images/logos/logo_download_white.svg';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { AppDispatch } from '../../../core/store';
import { getMsiValueRequestAction, resetMsiValueAction } from '../../../features/value/valueSlice';
import { getDcfValueRequestAction, resetDcfValueAction } from '../../../features/dcf/dcfSlice';
import { getSubscribedMsiVesselsRequest } from '../../../user-management/appSlice';
import { selectSelectedSubscriptionFormData, selectUserSubscriptionsFormData } from '../../sharedSelectors';
import { EOutputType, ESelectionType, ESubscriptionLevelType } from '../../sharedEnums';
import { selectSelectionData } from '../../../features/search/searchSelectors';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import {
	greenActions,
	selectFleetCiiAlignment,
	selectGreenSummaryData,
	selectVesselCiiAlignments,
} from '../../../features/green/greenSlice';
import { selectMsiValueData } from '../../../features/value/valueSelector';
import { selectDcfValueData } from '../../../features/dcf/dcfSelectors';
import { selectCurrentYearVesselCiiAlignmentData, selectSummaryAvgClimateAlignment } from '../../../features/green/greenSelector';
import { formatAmount, formatValueAsString } from '../../../utils/helpers';
import { EMenuItems, IMenuItems } from './rightSidebarTypes';
import { MenuItemButton } from './OutputMenuRenderers';
import { addSeascapeLogActivity } from '../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../activity-logging/activityLogTypes';
import { selectIsMsi } from '../../../user-management/appSelectors';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../features/date-range/slices/dateRangeSlice';
import { selectIsAnalysisDateRangeCoveringLive } from '../../../features/date-range/dataRangeSelector';
import { selectFeature, setFeature, setSelection, setSubtab, setTab } from '../../../routing/pageContextSlice';
import { selectVesselsLastSignalLoading } from '../../../features/main-map/mapSelectors';
import { selectActivityCoverage } from '../../../features/kpis/kpiSelectors';
import { kpisActions } from '../../../features/kpis/kpiSlice';

const FORMAT_OPTIONS = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
const FORMAT_OPTIONS_COVERAGE = { minimumFractionDigits: 1, maximumFractionDigits: 2 };

const ACTIVITY_LOG_EVENT_MAP: Record<string, SeascapeActivityLogConstants> = {
	value: SeascapeActivityLogConstants.VALUE_CLICKED,
	green: SeascapeActivityLogConstants.GREEN_CLICKED,
	dcf: SeascapeActivityLogConstants.DCF_CLICKED,
	kpis: SeascapeActivityLogConstants.ACTIVITY_CLICKED,
};

const getLogo = (isDisabled: boolean, isActive: boolean, grey: string, white: string, normal: string): string =>
	isDisabled ? grey : isActive ? white : normal;

// const formatCoverage = (value: string | number | null | undefined): string => value;

const OutputMenu: FC = () => {
	const dispatch = useAppDispatch<AppDispatch>();

	const isMsi = useAppSelector(selectIsMsi);
	const userSubscriptions = useAppSelector(selectUserSubscriptionsFormData);
	const selectedSubscription = useAppSelector(selectSelectedSubscriptionFormData);
	const selectionData = useAppSelector(selectSelectionData);
	const vesselCiiAlignmentsData = useAppSelector(selectVesselCiiAlignments);
	const fleetCiiAlignmentData = useAppSelector(selectFleetCiiAlignment);
	const currentYearVesselCiiAlignmentData = useAppSelector(selectCurrentYearVesselCiiAlignmentData);
	const summaryAvgClimateAlignment = useAppSelector(selectSummaryAvgClimateAlignment);
	const msiValueData = useAppSelector(selectMsiValueData);
	const dcfValueData = useAppSelector(selectDcfValueData);
	const greenSummaryData = useAppSelector(selectGreenSummaryData);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);
	const isDateRangeCoveringLive = useAppSelector(selectIsAnalysisDateRangeCoveringLive);
	const selectedFeature = useAppSelector(selectFeature);
	const isVesselsLastSignalLoading = useAppSelector(selectVesselsLastSignalLoading);
	const activityCoverage = useAppSelector(selectActivityCoverage);

	const [activeItem, setActiveItem] = useState('');

	const subscriptionLevel = selectedSubscription.seascapeActivityLevel;
	const isBronze = subscriptionLevel === ESubscriptionLevelType.Bronze;
	const isGold = subscriptionLevel === ESubscriptionLevelType.Gold;
	const isNoSelection = selectionData.outputType === EOutputType.None;
	const isGreenLoading =
		vesselCiiAlignmentsData?.loading || fleetCiiAlignmentData?.loading || greenSummaryData.loading || isVesselsLastSignalLoading;

	useEffect(() => {
		setActiveItem(selectedFeature ? selectedFeature.toLowerCase() : '');
	}, [selectedFeature]);

	useEffect(() => {
		if (selectionData.selectedVesselsList.length > 0) {
			const imoList = selectionData.selectedVesselsList.map((v) => v.imoNumber);

			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;

			const startDate = analysisDateRange.dateRange?.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange?.end.toDate('UTC').toISOString();

			if (!startYearMonth || !endYearMonth || !startDate || !endDate) return;

			dispatch(
				kpisActions.getActivityCoverageRequestAction({
					imoList,					
					startYearMonth,
					endYearMonth,
					startDate,
					endDate,
				}),
			);
			dispatch(getMsiValueRequestAction(imoList));
			dispatch(getDcfValueRequestAction(imoList));
			if (selectionData.outputType === EOutputType.Vessel) {
				dispatch(greenActions.getVesselCiiAlignmentsRequestAction({ imoNumber: imoList[0], startYearMonth, endYearMonth }));
			} else if (selectionData.outputType === EOutputType.Fleet) {
				dispatch(greenActions.getFleetCiiAlignmentRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }));
			} else if (selectionData.outputType === EOutputType.Summary) {
				dispatch(greenActions.getGreenSummaryRequestAction({ imoNumbers: imoList, startYearMonth, endYearMonth }));
			}
		}
	}, [dispatch, selectionData.selectedVesselsList, selectionData.outputType, analysisDateRange]);

	const resetRouteRelatedContext = useCallback(() => {
		dispatch(setFeature(null));
		dispatch(setTab(undefined));
		dispatch(setSubtab(undefined));
	}, [dispatch]);

	const resetValues = useCallback(() => {
		dispatch(resetMsiValueAction());
		dispatch(resetDcfValueAction());
		dispatch(setSelection({ type: ESelectionType.None }));
		resetRouteRelatedContext();
	}, [dispatch, resetRouteRelatedContext]);

	const onSideButtonClick = useCallback(
		(key: string) => {
			const lowerKey = key.toLowerCase();

			resetRouteRelatedContext();
			dispatch(setFeature(lowerKey));

			const eventId = ACTIVITY_LOG_EVENT_MAP[lowerKey];
			if (eventId) {
				const payload = JSON.stringify({ Name: lowerKey, OutputType: selectionData.outputType });
				addSeascapeLogActivity({ eventId, payload });
			}
		},
		[dispatch, resetRouteRelatedContext, selectionData.outputType],
	);

	const onSubscriptionChange = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			resetValues();
			const selectedSubscriptionData = userSubscriptions.find((s) => s?.id.toString() === event.target.value);
			if (selectedSubscriptionData) {
				dispatch(getSubscribedMsiVesselsRequest({ selectedSubscriptionData }));
			}
		},
		[dispatch, userSubscriptions, resetValues],
	);

	const greenValue = useMemo(() => {
		if (isBronze) return 'Not Subscribed';
		if (isGreenLoading) return <LoadingSpinner />;

		const { outputType } = selectionData;
		if (outputType === EOutputType.Vessel && currentYearVesselCiiAlignmentData?.ciiAlignment)
			return formatValueAsString(currentYearVesselCiiAlignmentData.ciiAlignment, FORMAT_OPTIONS, '', '', '%');
		if (outputType === EOutputType.Fleet && fleetCiiAlignmentData?.fleetCiiAlignmentData?.ciiAlignment)
			return formatValueAsString(fleetCiiAlignmentData.fleetCiiAlignmentData.ciiAlignment, FORMAT_OPTIONS, '', '', '%');
		if (outputType === EOutputType.Summary && summaryAvgClimateAlignment?.avgClimateAlignment)
			return formatValueAsString(summaryAvgClimateAlignment.avgClimateAlignment, FORMAT_OPTIONS, '', '', '%');
		return '';
	}, [
		isBronze,
		isGreenLoading,
		selectionData,
		currentYearVesselCiiAlignmentData,
		fleetCiiAlignmentData,
		summaryAvgClimateAlignment,
	]);

	const greenCoverage = useMemo(() => {
		if (isBronze) return 'Not Subscribed';
		if (isGreenLoading) return '';

		const { outputType } = selectionData;
		if (outputType === EOutputType.Vessel && currentYearVesselCiiAlignmentData?.ciiAlignment) return 100;
		if (outputType === EOutputType.Fleet && fleetCiiAlignmentData?.fleetCiiAlignmentData)
			return fleetCiiAlignmentData.fleetCiiAlignmentData.coveragePercentage;
		if (outputType === EOutputType.Summary && greenSummaryData.greenSummaryData && summaryAvgClimateAlignment.coveragePercentage)
			return formatValueAsString(summaryAvgClimateAlignment.coveragePercentage, FORMAT_OPTIONS_COVERAGE, '', '', '');

		return null;
	}, [
		isBronze,
		isGreenLoading,
		selectionData,
		currentYearVesselCiiAlignmentData,
		fleetCiiAlignmentData,
		greenSummaryData.greenSummaryData,
		summaryAvgClimateAlignment,
	]);

	const menuItems: IMenuItems[] = useMemo(() => {
		const isActive = (name: EMenuItems) => activeItem === name.toLowerCase();

		const items: IMenuItems[] = [
			{
				name: EMenuItems.KPIS,
				logo: getLogo(!isGold, isActive(EMenuItems.KPIS), logo_activity_grey, logo_activity_white, logo_activity),
				value: (activityCoverage?.loading && <LoadingSpinner />) || (!isGold ? 'Not Subscribed' : ''),
				disabled: !isGold || isNoSelection || activityCoverage?.activityCoverageData?.coveragePercentage === 0,
				isActive: isActive(EMenuItems.KPIS),
				activeColor: 'msi-darkgrey-color',
				coverage: activityCoverage?.activityCoverageData ? activityCoverage.activityCoverageData.coveragePercentage : '',
			},
			{
				name: EMenuItems.GREEN,
				logo: getLogo(isBronze, isActive(EMenuItems.GREEN), logo_green_grey, logo_green_white, logo_green),
				value: greenValue,
				disabled:
					isBronze ||
					isNoSelection ||
					(selectionData.outputType === EOutputType.Vessel && !currentYearVesselCiiAlignmentData?.ciiAlignment) ||
					(selectionData.outputType === EOutputType.Fleet && !fleetCiiAlignmentData?.fleetCiiAlignmentData?.ciiAlignment) ||
					(selectionData.outputType === EOutputType.Summary && !summaryAvgClimateAlignment?.avgClimateAlignment),
				isActive: isActive(EMenuItems.GREEN),
				activeColor: 'msi-chart-midgreen',
				coverage: greenCoverage,
			},
			{
				name: EMenuItems.VALUE,
				logo: (!isDateRangeCoveringLive && logo_value_gray) || (isActive(EMenuItems.VALUE) && logo_value_white) || logo_value,
				value: !isDateRangeCoveringLive
					? ''
					: ((msiValueData?.loading || isVesselsLastSignalLoading) && <LoadingSpinner />) ||
						formatAmount(msiValueData?.msiValueData?.msiValue ?? 0),
				coverage:
					!isDateRangeCoveringLive || msiValueData?.loading || isVesselsLastSignalLoading
						? ''
						: msiValueData?.msiValueData?.coveragePercentage || '',
				disabled: !isDateRangeCoveringLive || isNoSelection || msiValueData?.msiValueData?.coveragePercentage === 0,
				isActive: isActive(EMenuItems.VALUE),
				activeColor: 'msi-midblue-color-500',
				toolTip: !isDateRangeCoveringLive
					? 'Value is available for date ranges covering live data only'
					: msiValueData?.msiValueData?.coveragePercentage === 0
						? 'No coverage for selected vessel(s) in the selected date range'
						: undefined,
			},
			{
				name: EMenuItems.DCF,
				logo: getLogo(!isDateRangeCoveringLive || isBronze, isActive(EMenuItems.DCF), logo_dcf_grey, logo_dcf_white, logo_dcf),
				value: !isDateRangeCoveringLive ? (
					''
				) : isBronze ? (
					'Not Subscribed'
				) : dcfValueData?.loading || isVesselsLastSignalLoading ? (
					<LoadingSpinner />
				) : (
					formatAmount(dcfValueData!.dcfValueData?.dcfValue ?? 0)
				),
				coverage:
					!isDateRangeCoveringLive || isBronze || dcfValueData?.loading || isVesselsLastSignalLoading
						? ''
						: dcfValueData?.dcfValueData?.coveragePercentage || '',
				disabled: !isDateRangeCoveringLive || isBronze || isNoSelection || dcfValueData?.dcfValueData?.coveragePercentage === 0,
				isActive: isActive(EMenuItems.DCF),
				activeColor: 'msi-chart-orange',
				toolTip: !isDateRangeCoveringLive ? 'DCF is available for date ranges covering live data only' : undefined,
			},
		];

		if (isMsi) {
			items.push({
				name: EMenuItems.DOWNLOAD,
				logo: getLogo(isBronze, isActive(EMenuItems.DOWNLOAD), logo_download_grey, logo_download_white, logo_download),
				value: !isGold ? 'Not Subscribed' : '',
				disabled: isBronze,
				isActive: isActive(EMenuItems.DOWNLOAD),
				activeColor: 'msi-download-color',
			});
		}

		return items;
	}, [
		activeItem,
		activityCoverage,
		currentYearVesselCiiAlignmentData?.ciiAlignment,
		dcfValueData,
		fleetCiiAlignmentData?.fleetCiiAlignmentData?.ciiAlignment,
		greenCoverage,
		greenValue,
		isBronze,
		isDateRangeCoveringLive,
		isGold,
		isMsi,
		isNoSelection,
		isVesselsLastSignalLoading,
		msiValueData,
		selectionData.outputType,
		summaryAvgClimateAlignment?.avgClimateAlignment,
	]);

	return (
		<div className="fixed right-0 w-56 h-max bg-msi-white-color text-msi-midblue-color-500 z-30 rounded-none" style={{ top: 75 }}>
			{userSubscriptions && userSubscriptions.length > 1 && (
				<div className="flex space-x-2 bg-msi-white-color justify-center p-1">
					<Select
						disallowEmptySelection
						radius="none"
						selectionMode="single"
						placeholder="Select a subscription level"
						label="Selected Subscription"
						size="sm"
						onChange={onSubscriptionChange}
						className="max-w-xs rounded-none"
						classNames={{ popoverContent: 'rounded-none' }}
						defaultSelectedKeys={[selectedSubscription.id.toString()]}
					>
						{userSubscriptions.map((subscription) => (
							<SelectItem
								className="rounded"
								hideSelectedIcon
								key={subscription.id}
								endContent={subscription.seascapeActivityLevel}
							>
								{subscription.name}
							</SelectItem>
						))}
					</Select>
				</div>
			)}

			<div className="flex items-start justify-start space-x-2 bg-msi-white-color p-1 pl-2">
				<p className="text-md text-msi-darkgrey-color font-bold">Output Type:</p>
				<p className="text-md font-bold text-msi-darkblue-color-300">{selectionData.outputType}</p>
			</div>
			<ul className="space-y-2 p-2">
				{menuItems.map((item, index) => (
					<li key={index} className="w-full h-full">
						<MenuItemButton item={item} onSideButtonClick={onSideButtonClick} isMsi={isMsi} />
					</li>
				))}
			</ul>
		</div>
	);
};

export default OutputMenu;
